"use client";

import { getWeeksInMonth, type CalendarDate } from "@internationalized/date";
import { useRef } from "react";
import {
  useCalendarCell,
  useCalendarGrid,
  useLocale,
  type CalendarAria,
} from "react-aria";
import { type CalendarState } from "react-stately";

import { cn } from "@trip/utils";

import { AngleLeftIcon } from "~/components/icons/angle-left";
import { AngleRightIcon } from "~/components/icons/angle-right";
import { Large } from "~/components/typography";
import { Button } from "~/components/ui/button";

function CalendarCell({
  state,
  date,
}: {
  date: CalendarDate;
  state: CalendarState;
}) {
  const ref = useRef(null);
  const {
    cellProps,
    buttonProps,
    isSelected,
    isOutsideVisibleRange,
    isDisabled,
    isUnavailable,
    formattedDate,
  } = useCalendarCell({ date }, state, ref);
  return (
    <td {...cellProps} className="p-px text-neutral-500">
      <div
        {...buttonProps}
        className={cn(
          "relative flex aspect-square flex-col items-center justify-center rounded-full",
          isDisabled ? "opacity-20" : "",
          isUnavailable ? "opacity-20" : "",
          !isSelected && !(isDisabled || isUnavailable)
            ? "text-prune-500 hover:bg-prune-200"
            : "",
          isSelected ? "bg-prune-500 text-white" : "",
        )}
        hidden={isOutsideVisibleRange}
        ref={ref}
      >
        <div>{formattedDate}</div>
      </div>
    </td>
  );
}

export function MeetingCalendar({
  state,
  title,
}: {
  state: CalendarState;
  title: CalendarAria["title"];
}) {
  const { gridProps, headerProps, weekDays } = useCalendarGrid({}, state);

  // Get the number of weeks in the month so we can render the proper number of rows.
  const weeksInMonth = getWeeksInMonth(
    state.visibleRange.start,
    useLocale().locale,
  );

  return (
    <div className="*:w-full">
      <div className="mb-4 flex items-center px-2">
        <Button
          onClick={() => {
            state.focusPreviousPage();
          }}
          size="circle"
          variant="ghost"
          disabled={
            state.minValue
              ?.set({ month: state.visibleRange.start.month })
              .compare(state.visibleRange.start) === 0
          }
        >
          <AngleLeftIcon />
        </Button>
        <div className="grow text-center font-medium">
          <Large>{title}</Large>
        </div>
        <Button
          onClick={() => {
            state.focusNextPage();
          }}
          size="circle"
          variant="ghost"
        >
          <AngleRightIcon />
        </Button>
      </div>
      <table {...gridProps}>
        <thead {...headerProps}>
          <tr>
            {weekDays.map((day, index) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key
              <th key={`${day}-${index}`}>{day}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: weeksInMonth }, (_, weekIndex) => (
            <tr key={weekIndex}>
              {state
                .getDatesInWeek(weekIndex)
                .map((date) =>
                  date ? (
                    <CalendarCell
                      date={date}
                      key={date.toString()}
                      state={state}
                    />
                  ) : (
                    <td key={null} />
                  ),
                )}
            </tr>
          ))}
          {Array.from({ length: 6 - weeksInMonth }).map((_, index) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <tr key={index}>
              <td colSpan={7}>
                <div className="aspect-7/1 w-full" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
